import React, { useEffect, useState } from "react";
import api from "../lib/api";
import Turnstile from "../components/Turnstile";
import { Truck, Search, MapPin, Package } from "lucide-react";
import { useParams, Link } from "react-router-dom";

export default function PublicTrack() {
  const { lr } = useParams();
  const [lrInput, setLrInput] = useState(lr || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0); // bumping this remounts Turnstile for a fresh token

  const search = async (val) => {
    const v = (val || lrInput || "").trim().toUpperCase();
    if (!v) return;
    setLoading(true); setErr(""); setData(null);
    try {
      const r = await api.get(`/track/${v}`, {
        headers: captchaToken ? { "x-turnstile-token": captchaToken } : {},
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || "Could not find this shipment. Please check the LR number.");
    } finally {
      setLoading(false);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1); // Turnstile tokens are single-use — get a fresh one for the next search
    }
  };

  useEffect(() => { if (lr) search(lr); /* eslint-disable-next-line */ }, [lr]);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="hero-gradient text-white py-6 shadow-md">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/15 rounded-lg grid place-items-center"><Truck size={22}/></div>
            <div><div className="font-bold text-lg">Ritik Cargo Carriers</div><div className="text-xs text-sky-100">Track your shipment</div></div>
          </Link>
          <Link to="/login" className="text-sm bg-white/15 hover:bg-white/25 px-4 py-2 rounded-md backdrop-blur" data-testid="track-login-link">Staff Login</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="lt-card p-8 mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Track Your Consignment</h1>
          <p className="text-sm text-slate-500 mb-5">Enter your LR (Lorry Receipt) number to see real-time status and timeline.</p>
          <form onSubmit={(e)=>{e.preventDefault(); search();}} className="flex gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input data-testid="track-input" value={lrInput} onChange={e=>setLrInput(e.target.value)}
                placeholder="e.g. LR2026000001"
                className="w-full pl-10 pr-3 py-3 border border-slate-200 rounded-md focus:outline-none focus:border-sky-500 uppercase tracking-wide"/>
            </div>
            <button type="submit" disabled={loading} data-testid="track-submit"
              className="px-6 lt-btn-primary rounded-md font-semibold disabled:opacity-60">
              {loading ? "Searching..." : "Track"}
            </button>
          </form>
          <Turnstile key={captchaKey} action="track" onVerify={setCaptchaToken} />
          {err && <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-md" data-testid="track-error">{err}</div>}
        </div>

        {data && (
          <div className="lt-card overflow-hidden" data-testid="track-result">
            <div className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-slate-400">LR Number · {data.carrier}</div>
                <div className="text-xl font-bold tracking-wide">{data.lr_number}</div>
              </div>
              <div className="px-3 py-1.5 rounded-md bg-sky-500/20 text-sky-200 text-sm font-medium">Current Status: {data.status}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 p-6 border-b">
              <div><div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12}/>Origin</div><div className="font-semibold mt-0.5">{data.origin || "—"}</div></div>
              <div><div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12}/>Destination</div><div className="font-semibold mt-0.5">{data.destination || "—"}</div></div>
              <div><div className="text-xs text-slate-500 flex items-center gap-1"><Package size={12}/>Boxes / Weight</div><div className="font-semibold mt-0.5">{data.boxes} pkgs · {data.weight} kg</div></div>
              <div><div className="text-xs text-slate-500">Booked Date</div><div className="font-semibold mt-0.5">{data.lr_date}</div></div>
              <div className="md:col-span-2"><div className="text-xs text-slate-500">Shipper</div><div className="mt-0.5">{data.shipper}</div></div>
              <div className="md:col-span-2"><div className="text-xs text-slate-500">Consignee</div><div className="mt-0.5">{data.consignee}</div></div>
            </div>
            <div className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Tracking Timeline</h3>
              <div data-testid="track-timeline">
                {data.timeline.length === 0 ? <div className="text-sm text-slate-500">No tracking updates yet.</div> :
                  data.timeline.map((t, i) => {
                    const last = i === data.timeline.length - 1;
                    return (
                      <div key={t.id} className={`timeline-step ${last ? "current" : "completed"}`}>
                        <div className="dot"></div>
                        <div className="flex items-baseline justify-between flex-wrap gap-2">
                          <div className="font-semibold text-slate-900">{t.status}</div>
                          <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-sm text-slate-600">{t.location}{t.remarks && ` · ${t.remarks}`}</div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        )}
      </div>
      <footer className="text-center text-xs text-slate-500 py-6">© {new Date().getFullYear()} Ritik Cargo Carriers. All rights reserved.</footer>
    </div>
  );
}
